import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Download, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { LookupSelect } from "@/components/admin/LookupSelect";
import {
  listPayroll, upsertPayroll, deletePayroll, type PayrollRecord,
  listDocuments, uploadDocument, deleteDocument, getDocumentUrl,
  type EmployeeDocument,
} from "@/lib/hr-extra";
import { listAllotmentHistory, markAssetReturned } from "@/lib/hr";
import { todayIstIso } from "@/lib/tz";

export const Route = createFileRoute("/_app/_hr/hr/profile/$id")({
  component: ProfilePage,
  head: () => ({ meta: [{ title: "Employee Profile — HR" }] }),
});

interface ProfileFull {
  id: string;
  full_name: string;
  email: string;
  employee_id: string | null;
  department: string;
  status: string;
  avatar_url: string | null;
}

function ProfilePage() {
  const { id } = Route.useParams();
  const { profile: me } = useAuth();

  const { data: profile } = useQuery({
    queryKey: ["hr-profile", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email, employee_id, department, status, avatar_url")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as ProfileFull | null;
    },
  });

  if (!profile) return <div className="p-6 text-xs text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4 p-4">
      <Link to="/hr/directory" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Back to directory
      </Link>

      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-sm font-bold">
            {profile.full_name?.[0]?.toUpperCase() ?? "?"}
          </div>
          <div>
            <div className="text-base font-semibold">{profile.full_name}</div>
            <div className="text-xs text-muted-foreground">
              <span className="font-mono">#{profile.employee_id ?? "—"}</span> · {DEPARTMENT_LABELS[profile.department as Department] ?? profile.department} · {profile.email}
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="salary">
        <TabsList>
          <TabsTrigger value="salary">Salary</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="assets">Assets</TabsTrigger>
        </TabsList>
        <TabsContent value="salary"><SalaryTab employeeId={id} createdBy={me?.id ?? null} /></TabsContent>
        <TabsContent value="documents"><DocumentsTab employeeId={id} uploadedBy={me?.id ?? null} /></TabsContent>
        <TabsContent value="assets"><AssetsTab employeeId={id} /></TabsContent>
      </Tabs>
    </div>
  );
}

function SalaryTab({ employeeId, createdBy }: { employeeId: string; createdBy: string | null }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PayrollRecord | null>(null);
  const [form, setForm] = useState({
    pay_period_start: "", pay_period_end: "", base_salary: "0", bonus: "0",
    deductions: "0", net_pay: "0", payment_date: "", payment_method: "", notes: "",
  });

  const { data: records = [] } = useQuery({
    queryKey: ["payroll", employeeId],
    queryFn: () => listPayroll(employeeId),
  });

  const reset = () => {
    setEditing(null);
    setForm({ pay_period_start: "", pay_period_end: "", base_salary: "0", bonus: "0", deductions: "0", net_pay: "0", payment_date: "", payment_method: "", notes: "" });
  };

  const openNew = () => { reset(); setOpen(true); };
  const openEdit = (r: PayrollRecord) => {
    setEditing(r);
    setForm({
      pay_period_start: r.pay_period_start, pay_period_end: r.pay_period_end,
      base_salary: String(r.base_salary), bonus: String(r.bonus), deductions: String(r.deductions),
      net_pay: String(r.net_pay), payment_date: r.payment_date ?? "", payment_method: r.payment_method ?? "", notes: r.notes ?? "",
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: () => upsertPayroll({
      id: editing?.id,
      employee_id: employeeId,
      pay_period_start: form.pay_period_start,
      pay_period_end: form.pay_period_end,
      base_salary: Number(form.base_salary),
      bonus: Number(form.bonus),
      deductions: Number(form.deductions),
      net_pay: Number(form.net_pay),
      payment_date: form.payment_date || null,
      payment_method: form.payment_method || null,
      notes: form.notes || null,
      created_by: createdBy,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll", employeeId] });
      toast.success("Saved");
      setOpen(false); reset();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (rid: string) => deletePayroll(rid),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["payroll", employeeId] }); toast.success("Deleted"); },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Payroll History</h2>
        <Button size="sm" onClick={openNew}><Plus className="mr-1 h-3 w-3" /> Add Pay Period</Button>
      </div>
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Base</TableHead>
              <TableHead className="text-right">Bonus</TableHead>
              <TableHead className="text-right">Deductions</TableHead>
              <TableHead className="text-right">Net Pay</TableHead>
              <TableHead>Paid On</TableHead>
              <TableHead>Method</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-xs text-muted-foreground">No records</TableCell></TableRow>
            )}
            {records.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="text-xs">{r.pay_period_start} → {r.pay_period_end}</TableCell>
                <TableCell className="text-right text-xs">{r.base_salary}</TableCell>
                <TableCell className="text-right text-xs">{r.bonus}</TableCell>
                <TableCell className="text-right text-xs">{r.deductions}</TableCell>
                <TableCell className="text-right text-xs font-semibold">{r.net_pay}</TableCell>
                <TableCell className="text-xs">{r.payment_date ?? "—"}</TableCell>
                <TableCell className="text-xs">{r.payment_method ?? "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => openEdit(r)}>Edit</Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => del.mutate(r.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Pay Period</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs">Period Start</label>
              <Input type="date" value={form.pay_period_start} onChange={(e) => setForm({ ...form, pay_period_start: e.target.value })} className="h-9 text-xs" /></div>
            <div><label className="text-xs">Period End</label>
              <Input type="date" value={form.pay_period_end} onChange={(e) => setForm({ ...form, pay_period_end: e.target.value })} className="h-9 text-xs" /></div>
            <div><label className="text-xs">Base Salary</label>
              <Input type="number" step="0.01" value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })} className="h-9 text-xs" /></div>
            <div><label className="text-xs">Bonus</label>
              <Input type="number" step="0.01" value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value })} className="h-9 text-xs" /></div>
            <div><label className="text-xs">Deductions</label>
              <Input type="number" step="0.01" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} className="h-9 text-xs" /></div>
            <div><label className="text-xs">Net Pay</label>
              <Input type="number" step="0.01" value={form.net_pay} onChange={(e) => setForm({ ...form, net_pay: e.target.value })} className="h-9 text-xs" /></div>
            <div><label className="text-xs">Payment Date</label>
              <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="h-9 text-xs" /></div>
            <div><label className="text-xs">Method</label>
              <Input value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="h-9 text-xs" placeholder="Bank Transfer" /></div>
            <div className="col-span-2"><label className="text-xs">Notes</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="text-xs" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocumentsTab({ employeeId, uploadedBy }: { employeeId: string; uploadedBy: string | null }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const { data: docs = [] } = useQuery({
    queryKey: ["emp-docs", employeeId],
    queryFn: () => listDocuments(employeeId),
  });

  const up = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Pick a file");
      if (!docType) throw new Error("Select document type");
      if (!title.trim()) throw new Error("Title is required");
      return uploadDocument({ employee_id: employeeId, doc_type: docType, title: title.trim(), file, notes, uploaded_by: uploadedBy });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emp-docs", employeeId] });
      toast.success("Uploaded");
      setOpen(false); setFile(null); setTitle(""); setNotes(""); setDocType("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (d: EmployeeDocument) => deleteDocument(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["emp-docs", employeeId] }); toast.success("Deleted"); },
  });

  const open_ = async (d: EmployeeDocument) => {
    try { window.open(await getDocumentUrl(d.file_path), "_blank"); }
    catch (e) { toast.error((e as Error).message); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Documents</h2>
        <Button size="sm" onClick={() => setOpen(true)}><Upload className="mr-1 h-3 w-3" /> Upload</Button>
      </div>
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground">No documents</TableCell></TableRow>
            )}
            {docs.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="text-xs font-medium">{d.title}</TableCell>
                <TableCell className="text-xs">{d.doc_type}</TableCell>
                <TableCell className="text-xs">{d.created_at.slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => open_(d)}>
                    <Download className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => del.mutate(d)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs">Document Type</label>
              <LookupSelect listKey="hr_document_types" value={docType} onChange={setDocType} />
              <p className="mt-1 text-[10px] text-muted-foreground">Salary Slip is intentionally excluded.</p>
            </div>
            <div>
              <label className="text-xs">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-9 text-xs" />
            </div>
            <div>
              <label className="text-xs">File</label>
              <Input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="h-9 text-xs" />
            </div>
            <div>
              <label className="text-xs">Notes</label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => up.mutate()} disabled={up.isPending}>Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssetsTab({ employeeId }: { employeeId: string }) {
  const qc = useQueryClient();
  const { data: history = [] } = useQuery({
    queryKey: ["allotment-history", employeeId],
    queryFn: () => listAllotmentHistory(employeeId),
  });
  const ret = useMutation({
    mutationFn: ({ id, returned_at }: { id: string; returned_at: string | null }) => markAssetReturned(id, returned_at),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["allotment-history", employeeId] }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Allotted Assets</h2>
        <Link to="/hr/assets" className="text-xs text-primary hover:underline">Allot new →</Link>
      </div>
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Serial</TableHead>
              <TableHead>Locker</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-xs text-muted-foreground">No assets allotted</TableCell></TableRow>
            )}
            {history.map((h) => (
              <TableRow key={h.id}>
                <TableCell className="text-xs">{h.allotment_date}</TableCell>
                <TableCell className="text-xs">{h.asset_name}</TableCell>
                <TableCell className="text-xs">{h.asset_type}</TableCell>
                <TableCell className="text-xs font-mono">{h.serial_number || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{h.locker_number || "—"}</TableCell>
                <TableCell>
                  {h.returned_at ? (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px]">Returned {h.returned_at}</span>
                  ) : (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] text-green-800 dark:bg-green-900/40 dark:text-green-200">Active</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {!h.returned_at && (
                    <Button size="sm" variant="outline" className="h-7 text-[10px]"
                      onClick={() => ret.mutate({ id: h.id, returned_at: todayIstIso() })}>
                      Mark Returned
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
